import { Stack, Typography, useTheme } from "@mui/material";
import type { Control } from "react-hook-form";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import {
	CONTRACT_REQUIRED_MINIMUM_FIELDS,
	getContractRequiredMinimumMessage,
} from "web_myplann/Portfolio/Forms/ContractShared/contractRequiredMinimum";
import { PensionAdditionalFields } from "web_myplann/Portfolio/Forms/PensionForm/PensionAdditionalFields";
import { PensionBasicFields } from "web_myplann/Portfolio/Forms/PensionForm/PensionBasicFields";
import { PensionContractFields } from "web_myplann/Portfolio/Forms/PensionForm/PensionContractFields";
import type {
	PensionCreateFormInput,
	PensionCreateFormOutput,
} from "web_myplann/Portfolio/Forms/PensionForm/pensionFormSchema";
import { RequiredMinimumMessage } from "web_myplann/Portfolio/PortfolioShared/RequiredMinimumMessage";
import { DisclosureSection } from "web_myplann/Shared/Components/DisclosureSection";
import { FlatRowFormDatePicker } from "web_myplann/Shared/Components/FlatRow/FlatRowFormDatePicker";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { DateView } from "web_myplann/Shared/Types/DateView";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";

type PensionCreateFormFieldsProps = {
	control: Control<PensionCreateFormInput, unknown, PensionCreateFormOutput>;
};

export const PensionCreateFormFields = ({
	control,
}: PensionCreateFormFieldsProps) => {
	const theme = useTheme();
	const { t } = useResource();
	const today = new Date();

	return (
		<>
			<PensionBasicFields
				control={control}
				namePlaceholder={t(
					Resources.Portfolio.AddPension.Placeholders.nameCreate,
				)}
				contractNumberPlaceholder={t(
					Resources.Portfolio.AddShared.Placeholders.fill,
				)}
			/>
			<RequiredMinimumMessage
				control={control}
				fields={CONTRACT_REQUIRED_MINIMUM_FIELDS}
				message={getContractRequiredMinimumMessage(
					t,
					Resources.Portfolio.AddPension.Fields,
				)}
			/>

			<Typography variant="sectionLabel">
				{t(Resources.Portfolio.AddPension.Sections.values)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<DisclosureSection
					labelCollapsed={t(
						Resources.Portfolio.AddPension.Sections.showParameters,
					)}
					labelExpanded={t(
						Resources.Portfolio.AddPension.Sections.hideParameters,
					)}
				>
					<FlatRowFormInput
						control={control}
						name="value"
						type="number"
						label={t(Resources.Portfolio.AddPension.Fields.value)}
						placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
						suffix="Kč"
					/>
					<FlatRowFormInput
						control={control}
						name="invested"
						type="number"
						label={t(Resources.Portfolio.AddPension.Fields.invested)}
						placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
						suffix="Kč"
					/>
					<FlatRowFormDatePicker
						control={control}
						name="valueDate"
						label={t(Resources.Portfolio.AddPension.Fields.valueDate)}
						views={[DateView.Year, DateView.Month, DateView.Day]}
						displayFormat={DateFormat.Display}
						maxDate={today}
					/>
					<PensionContractFields control={control} />
				</DisclosureSection>
			</Stack>

			<PensionAdditionalFields control={control} />
		</>
	);
};
